import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import _ from 'lodash'
import { Table, Spin, Dropdown, Menu, Space, Button } from 'antd'
import { getEquipmentsAction, equipmentSelector } from '../../../store/equipment'
import { DownloadOutlined, DownOutlined } from '@ant-design/icons'
import { OPERATIONS, EQUIPMENT_WORK_FLOW, DEFAULT_EQUIPMENT_STATUSES, MAX_ROWS_EXPORT } from '../constants'
import EquipmentsFilterPanel from './EquipmentsGroupFilterPanel'
import { formatDateTime } from '../../../utils/date-format'
import { exportRowsToXlsx } from '../../../utils/export-xls'
import { userSelector } from '../../../store/session'
import EquipmentDetailModal from './EquipmentDetailModal'
import { ProtectedAccess } from '../../RoleBasedAccess'
import useProtectedTableColumns from '../../hooks/useProtectedTableColumns'

const mapCurrentStatus = (status) => EQUIPMENT_WORK_FLOW[status]?.caption

const mapCurrentPlace = ({ current_project, current_stockroom }) => {
  if (current_project) return current_project
  else if (current_stockroom) return current_stockroom
  else return '-'
}

const mapTargetPlace = (inventories) => {
  let last = _.last(_.sortBy(inventories, 'id'))
  if (last) return last.target_project ? last.target_project?.name : last.target_stockroom?.name
  else return '-'
}

const renderUser = (user) => (user ? `${user.second_name} ${user.first_name} (${user.email})` : '-')

const renderDescription = (value) => (value != null ? value : '-')

const filterEquipmentStatus = (equipments, operation) => {
  return equipments.filter(({ current_status }) => EQUIPMENT_WORK_FLOW[current_status].actions.includes(operation))
}

const EquipmentsTable = ({ onEditRecord, onDeleteRecord, onInventoryOperation, addNewAction }) => {
  const dispatch = useDispatch()
  const { user } = useSelector(userSelector)
  const { isFetching, pagination, equipments } = useSelector(equipmentSelector)

  const [page, setPage] = useState(1)
  const [selectedRows, setSelectedRows] = useState([])

  const [showEquipmentDetail, setShowEquipmentDetail] = useState(false)
  const [equipmentDetailId, setEquipmentDetailId] = useState(null)

  const [filters, setFilters] = useState({ current_status: DEFAULT_EQUIPMENT_STATUSES })

  const isProject = useMemo(() => user?.role?.type === 'project_manager', [user])

  const tablePagination = useMemo(
    () => ({
      current: page,
      pageSize: 10,
      total: pagination?.total,
      onChange: setPage,
      showSizeChanger: false,
    }),
    [pagination]
  )

  useEffect(() => pagination && pagination.page && setPage(Number(pagination.page)), [pagination])
  useEffect(() => loadData(filters, buildPagination()), [dispatch, filters])
  useEffect(() => page !== pagination?.page && loadData(filters, buildPagination(page)), [dispatch, page])

  const buildPagination = (page = 1, size = 10) => ({ page, pageSize: size })

  const loadData = (filters, pagination) => dispatch(getEquipmentsAction({ filters, pagination }))

  const onRecordEditPress = (record) => onEditRecord(record)

  const onRecordDeletePress = (record) => onDeleteRecord(record)

  const onSearch = useCallback((filter) => setFilters(filter), [])

  const viewEquipmentDetail = useCallback((equipment) => {
    setEquipmentDetailId(equipment)
    setShowEquipmentDetail(true)
  }, [])

  const hideEquipmentDetail = useCallback(() => {
    setEquipmentDetailId(null)
    setShowEquipmentDetail(false)
  }, [])

  const exportData = useCallback(async () => {
    const header = [
      [
        '№ п/п',
        'Наименование',
        'Цена',
        'Серийный номер',
        'Год выпуска',
        'Описание',
        'Текущее местоположение',
        'Статус',
        'ФИО сотрудника',
        'Дата формирования',
      ],
    ]

    let data = await loadData(filters, buildPagination(1, MAX_ROWS_EXPORT))
    let rows = data.payload.data
      ?.flatMap((e) => (e.children ? e.children : [e]))
      .map((e) => [
        e.id,
        e.name,
        e.price,
        e.serial_number,
        e.issue_year,
        e.description,
        mapCurrentPlace(e),
        mapCurrentStatus(e.current_status),
        renderUser(e.create_user),
        formatDateTime(e.createdAt),
      ])

    rows && exportRowsToXlsx(rows, header, 'Отчет_по_оборудованию.xlsx')
  }, [filters])

  const handleOperationClick = useCallback(
    ({ id, children }, name) => {
      const ids = hasSelected ? filterEquipmentStatus(selectedRows, name).map(({ id }) => id) : children || [id]
      onInventoryOperation(ids, name)
    },
    [selectedRows]
  )

  const renderOperationsMenu = (_, record) => {
    const operations = Object.entries(OPERATIONS).filter(([name, _]) =>
      EQUIPMENT_WORK_FLOW[record.current_status].actions.includes(name)
    )

    const menu = (
      <Menu>
        {operations.map(([name, { caption }]) => (
          <Menu.Item key={`operation-${name}`} onClick={() => handleOperationClick(record, name)} type="dashed">
            {caption}
          </Menu.Item>
        ))}
      </Menu>
    )
    return (
      <Space>
        <Dropdown overlay={menu} disabled={operations.length === 0}>
          <a>
            Операции <DownOutlined />
          </a>
        </Dropdown>
      </Space>
    )
  }

  const renderActionsMenu = (_, record) => {
    const menu = (
      <Menu>
        <Menu.Item key="edit-draft" onClick={() => onRecordEditPress(record)} type="dashed">
          Редактировать
        </Menu.Item>
        <Menu.Item key="details" onClick={() => viewEquipmentDetail(record.id)} type="dashed">
          История операций
        </Menu.Item>
        {isProject && (
          <Menu.Item key="remove" onClick={() => onRecordDeletePress(record)} type="primary" danger>
            Удалить
          </Menu.Item>
        )}
      </Menu>
    )
    return (
      <Dropdown overlay={menu} disabled={record.children}>
        <a>
          Действия <DownOutlined />
        </a>
      </Dropdown>
    )
  }

  const hasSelected = useMemo(() => selectedRows.length > 0, [selectedRows])

  const onSelectChange = useCallback((_, selectedRows) => {
    const selected = selectedRows.flatMap(({ id, current_status, children }) =>
      children
        ? [{ id }, ...children.map(({ id, current_status }) => ({ id, current_status }))]
        : { id, current_status }
    )
    setSelectedRows(selected)
  }, [])

  const rowSelection = {
    selectedRowKeys: selectedRows.map(({ id }) => id),
    onChange: onSelectChange,
  }

  const columns = [
    {
      title: '№',
      dataIndex: 'id',
      key: 'id',
      align: 'right',
      width: 100,
    },
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name',
      width: 200,
    },
    {
      title: 'Заявка',
      dataIndex: 'application_id',
      key: 'application_id',
      width: 60,
    },
    {
      title: 'Статус',
      dataIndex: 'current_status',
      key: 'current_status',
      render: mapCurrentStatus,
      width: 100,
    },
    {
      title: 'Дата формирования',
      dataIndex: 'createdAt',
      key: 'created_date',
      render: formatDateTime,
      width: 130,
    },
    {
      title: 'Цена',
      dataIndex: 'price',
      key: 'price',
      width: 100,
    },
    {
      title: 'Описание',
      dataIndex: 'description',
      key: 'description',
      render: renderDescription,
      width: 160,
    },
    {
      title: 'Текущее местоположение',
      key: 'current_place',
      render: mapCurrentPlace,
      width: 200,
    },
    {
      title: 'Целевое местоположение',
      dataIndex: 'inventories',
      key: 'target_place',
      render: mapTargetPlace,
      width: 200,
    },
    {
      title: 'Пользователь создавший',
      dataIndex: 'create_user',
      key: 'create_user',
      render: renderUser,
      width: 250,
    },
    {
      title: 'Серийный номер',
      dataIndex: 'serial_number',
      key: 'serial_number',
      width: 140,
    },
    {
      title: 'Год выпуска',
      dataIndex: 'issue_year',
      key: 'issue_year',
      width: 80,
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 90,
      render: renderActionsMenu,
      fixed: 'right',
      allowedRoles: ['project_manager', 'foreman'],
    },
    {
      title: 'Операции',
      key: 'operations',
      width: 90,
      render: renderOperationsMenu,
      fixed: 'right',
      allowedRoles: ['project_manager', 'foreman'],
    },
  ]

  const [protectedColumns] = useProtectedTableColumns(columns)

  return (
    <Spin spinning={isFetching}>
      <div>
        <EquipmentsFilterPanel onSearch={onSearch} />
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <ProtectedAccess allowedRoles={['project_manager', 'foreman']}>
            <Button type="primary" onClick={addNewAction}>
              Добавить оборудование на склад
            </Button>
          </ProtectedAccess>
          <Button type="dashed" style={{ marginLeft: 15 }} icon={<DownloadOutlined />} onClick={exportData}>
            Выгрузить в XLS
          </Button>
        </div>
      </div>
      <Table
        rowKey="id"
        className="table-equipments"
        rowClassName={(record) => (record.children ? 'grouped-row' : '')}
        style={{ paddingTop: 20 }}
        pagination={tablePagination}
        columns={protectedColumns}
        dataSource={equipments}
        rowSelection={rowSelection}
        bordered
        size="small"
        scroll={{
          x: 2500,
        }}
      />
      <EquipmentDetailModal
        showModal={showEquipmentDetail}
        equipment={equipmentDetailId}
        onCancel={hideEquipmentDetail}
        onRefresh={() => loadData(filters, buildPagination(page))}
      />
    </Spin>
  )
}

export default EquipmentsTable
